import { TileCardDetails } from "../page/Interfaces";
import { calculateDistance } from "./calculateDistance";
import { FILTERS_KEYS, FILTER_PICKLEBALL_KEYS, SORT_KEYS, DISTANCE_RANGE_MAX } from "../components/FilterAndSort/constants/constants";
import { getStructuredWeekdayPrice } from "./pricing";
import { areCitiesEquivalent } from "../config/cityData";

export enum SortOrder {
  ASC = "ASC",
  DESC = "DESC",
}

// Generic data processing utilities
export const createDataProcessor = <T extends { data: any; name: string }>() => ({
  // City filtering
  filterByCity: (data: T[], city: string): T[] => {
    return data.filter((item) => {
      if (!item?.data) return false;
      
      const itemCity = item.data?.city;
      const targetCity = city;
      
      if (!itemCity || !targetCity) return false;

      return areCitiesEquivalent(itemCity, targetCity);
    });
  },

  // Search filtering
  filterBySearch: (data: T[], search: string): T[] => {
    if (!search.trim()) return data;
    return data.filter((item) =>
      item.name.toLowerCase().includes(search.toLowerCase())
    );
  },

  // Enhanced search filtering with address matching
  filterBySearchWithAddress: (data: T[], search: string): T[] => {
    if (!search.trim()) return data;
    return data.filter((item) => {
      const searchTerm = search.toLowerCase();
      const name = item.name.toLowerCase();
      const address = item?.data?.address?.toLowerCase() || '';
      const streetName = item?.data?.turfAddress?.streetName?.toLowerCase() || '';
      const landmark = item?.data?.turfAddress?.landmark?.toLowerCase() || '';
      
      return name.includes(searchTerm) || 
             address.includes(searchTerm) || 
             streetName.includes(searchTerm) || 
             landmark.includes(searchTerm);
    });
  },

  // Location-based filtering with address priority
  filterByLocationWithAddressPriority: (data: T[], userLocation: any, distanceRange: number): T[] => {
    if (!userLocation || distanceRange === DISTANCE_RANGE_MAX) return data;
    
    return data.filter((item) => {
      const location = item?.data?.location;
      if (!location || !location.latitude || !location.longitude) return false;
      
      const lat = Number(location.latitude);
      const lng = Number(location.longitude);
      
      if (isNaN(lat) || isNaN(lng)) return false;
      
      const distance = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        lat,
        lng
      );
      return distance <= distanceRange;
    });
  },

  // Distance filtering
  filterByDistance: (data: T[], userLocation: any, distanceRange: number): T[] => {
    if (!userLocation || distanceRange === DISTANCE_RANGE_MAX) return data;
    
    return data.filter((item) => {
      const location = item?.data?.location;
      if (!location || !location.latitude || !location.longitude) return false;
      
      const lat = Number(location.latitude);
      const lng = Number(location.longitude);
      
      if (isNaN(lat) || isNaN(lng)) return false;
      
      const distance = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        lat,
        lng
      );
      return distance <= distanceRange;
    });
  },

  // Generic sorting
  sortByPrice: (data: T[], sortOrder: SortOrder, getMinPrice: (item: T) => number): T[] => {
    return data.sort((a, b) => {
      const priceA = getMinPrice(a);
      const priceB = getMinPrice(b);
      return sortOrder === SortOrder.ASC ? priceA - priceB : priceB - priceA;
    });
  },

  sortByRating: (data: T[], sortOrder: SortOrder): T[] => {
    return data.sort((a, b) => {
      const ratingA = Number(a?.data?.starRating || 0);
      const ratingB = Number(b?.data?.starRating || 0);
      return sortOrder === SortOrder.ASC ? ratingA - ratingB : ratingB - ratingA;
    });
  },

  sortByDistance: (data: T[], userLocation: any): T[] => {
    if (!userLocation) return data;
    
    return data.sort((a, b) => {
      const locationA = a?.data?.location;
      const locationB = b?.data?.location;
      
      if (!locationA || !locationB) return 0;
      
      const distanceA = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        Number(locationA.latitude),
        Number(locationA.longitude)
      );
      
      const distanceB = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        Number(locationB.latitude),
        Number(locationB.longitude)
      );
      
      return distanceA - distanceB;
    });
  },

  // Sort with address priority over distance
  sortByLocationWithAddressPriority: (data: T[], userLocation: any, searchTerm?: string): T[] => {
    if (!userLocation) return data;
    
    return data.slice().sort((a, b) => {
      // Extract location names from user's selected location
      const userLocationName = userLocation.name?.toLowerCase() || '';
      const userAddress = userLocation.address?.toLowerCase() || '';
      
      // Check if turf addresses match the selected location
      const aAddress = a?.data?.address?.toLowerCase() || '';
      const aStreetName = a?.data?.turfAddress?.streetName?.toLowerCase() || '';
      const aLandmark = a?.data?.turfAddress?.landmark?.toLowerCase() || '';
      
      const bAddress = b?.data?.address?.toLowerCase() || '';
      const bStreetName = b?.data?.turfAddress?.streetName?.toLowerCase() || '';
      const bLandmark = b?.data?.turfAddress?.landmark?.toLowerCase() || '';
      
      // Priority scoring: Higher score = higher priority
      let aScore = 0;
      let bScore = 0;
      
      // Address matching priority (highest priority)
      const aAddressMatch = userLocationName && (
        aAddress.includes(userLocationName) || 
        aStreetName.includes(userLocationName) || 
        aLandmark.includes(userLocationName)
      );
      
      const bAddressMatch = userLocationName && (
        bAddress.includes(userLocationName) || 
        bStreetName.includes(userLocationName) || 
        bLandmark.includes(userLocationName)
      );
      if (aAddressMatch) {
        aScore += 1000; // High priority for address match
      }
      
      if (bAddressMatch) {
        bScore += 1000; // High priority for address match      
      }
      
      // Additional search term matching
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        if (aAddress.includes(searchLower) || aStreetName.includes(searchLower) || aLandmark.includes(searchLower)) {
          aScore += 500;
        }
        if (bAddress.includes(searchLower) || bStreetName.includes(searchLower) || bLandmark.includes(searchLower)) {
          bScore += 500;
        }
      }
      
      // Distance as tie-breaker (lower distance = higher priority)
      const locationA = a?.data?.location;
      const locationB = b?.data?.location;
      
      if (locationA && locationB) {
        const latA = Number(locationA?.latitude);
        const lngA = Number(locationA?.longitude);
        const latB = Number(locationB?.latitude);
        const lngB = Number(locationB?.longitude);
        
        if (!isNaN(latA) && !isNaN(lngA) && !isNaN(latB) && !isNaN(lngB)) {
          const distanceA = calculateDistance(userLocation.latitude, userLocation.longitude, latA, lngA);
          const distanceB = calculateDistance(userLocation.latitude, userLocation.longitude, latB, lngB);
          
          // Subtract distance from score (closer = higher score)
          const aDistanceScore = (100 - Math.min(distanceA, 100));
          const bDistanceScore = (100 - Math.min(distanceB, 100));
          
          aScore += aDistanceScore;
          bScore += bDistanceScore;
          
        }
      }
      // Return in descending order (higher score first)
      // For multiple turfs: positive = b comes first, negative = a comes first, 0 = equal
      const result = bScore - aScore;
      return result;
    });
  }
});

// Turf-specific utilities
export const turfProcessor = createDataProcessor<TileCardDetails>();

export const getTurfMinPrice = (turf: TileCardDetails): number => {
  let minPrice = Infinity;
  
  if (!turf?.data?.areaPrice || !Array.isArray(turf.data.areaPrice)) {
    return Infinity;
  }
  
  turf.data.areaPrice.forEach((area) => {
    if (area?.price?.weekday?.regularHours !== undefined) {
      const areaPrice = area.price.weekday.regularHours;
      if (areaPrice < minPrice) {
        minPrice = areaPrice;
      }
    }
  });
  return minPrice;
};

export const getTurfMaxHeight = (turf: TileCardDetails): number => {
  let maxHeight = -1;
  
  if (!turf?.data?.areaPrice || !Array.isArray(turf.data.areaPrice)) {
    return -1;
  }
  
  turf.data.areaPrice.forEach((area: any) => {
    if (Number(area?.pitchDimensions?.height) > 0) {
      const height = Number(area.pitchDimensions.height);
      if (height > maxHeight) {
        maxHeight = height;
      }
    }
  });
  return maxHeight;
};

// Court-specific utilities
export const courtProcessor = createDataProcessor<any>();

export const getCourtMinPrice = (court: any): number => {
  let minPrice = Infinity;
  
  if (!court?.data?.courtDetails || !Array.isArray(court.data.courtDetails)) {
    return Infinity;
  }
  
  court.data.courtDetails.forEach((courtDetail: any) => {
    const courtPrice = getStructuredWeekdayPrice(courtDetail?.price);

    if (courtPrice !== null) {
      if (courtPrice < minPrice) {
        minPrice = courtPrice;
      }
    }
  });
  return minPrice;
};

// Generic filtering functions
export const createFilterProcessor = () => ({
  // Turf filters
  filterTurfs: (filters: string[], turfData: TileCardDetails[]): TileCardDetails[] => {
    let filteredData = [...turfData];

    filters?.forEach((filterKey) => {
      switch (filterKey) {
        case FILTERS_KEYS.COVERED:
          filteredData = filteredData.filter(({ data }) => data.isRainProtected === true);
          break;
        case FILTERS_KEYS.OPEN_24_7:
          filteredData = filteredData.filter(({ data }) => data.availabilityData?.isAvailable24Hours === true);
          break;
        case FILTERS_KEYS.CAFE:
          filteredData = filteredData.filter(({ data }) => data.hasCafe === true);
          break;
        case FILTERS_KEYS.PARTNERED:
          filteredData = filteredData.filter(({ data }) => data.partnership === true);
          break;
        case FILTERS_KEYS.UNCOVERED:
          filteredData = filteredData.filter(({ data }) => data.isRainProtected === false);
          break;
      }
    });

    return filteredData;
  },

  // Court filters
  filterCourts: (filters: string[], courtData: any[]): any[] => {
    let filteredData = [...courtData];

    filters?.forEach((filterKey) => {
      switch (filterKey) {
        case FILTERS_KEYS.COVERED:
          filteredData = filteredData.filter(({ data }) => data.isRainProtected === true);
          break;
        case FILTERS_KEYS.OPEN_24_7:
          filteredData = filteredData.filter(({ data }) => data.availabilityData?.isAvailable24Hours === true);
          break;
        case FILTERS_KEYS.CAFE:
          filteredData = filteredData.filter(({ data }) => data.hasCafe === true);
          break;
        case FILTERS_KEYS.PARTNERED:
          filteredData = filteredData.filter(({ data }) => data.partnership === true);
          break;
        case FILTERS_KEYS.UNCOVERED:
          filteredData = filteredData.filter(({ data }) => data.isRainProtected === false);
          break;
        case FILTER_PICKLEBALL_KEYS.CONCRETE_COURT:
        case FILTER_PICKLEBALL_KEYS.ACRYLIC_COATED_COURT:
        case FILTER_PICKLEBALL_KEYS.SYNTHETIC_TURF_COURT:
        case FILTER_PICKLEBALL_KEYS.ASPHALT_COURT:
        case FILTER_PICKLEBALL_KEYS.MODULAR_TILE_COURT:
        case FILTER_PICKLEBALL_KEYS.CUSHIONED_ACRYLIC_COURT:
          filteredData = filteredData.filter((court) => {
            return court.data?.courtDetails?.some((courtDetail: any) => {
              const surface = courtDetail.surface?.toLowerCase();
              return getSurfaceMatch(filterKey, surface);
            });
          });
          break;
      }
    });

    return filteredData;
  }
});

// Surface matching utility
const getSurfaceMatch = (filterKey: string, surface: string): boolean => {
  const surfaceMap: Record<string, string> = {
    'CONCRETE_COURT': 'concrete',
    'ACRYLIC_COATED_COURT': 'acrylic',
    'SYNTHETIC_TURF_COURT': 'synthetic',
    'ASPHALT_COURT': 'asphalt',
    'MODULAR_TILE_COURT': 'modular',
    'CUSHIONED_ACRYLIC_COURT': 'cushioned'
  };
  
  return surfaceMap[filterKey] === surface;
};

// Sorting utilities
export const createSortProcessor = () => ({
  // Main turf sorting: address-priority when searching, explicit sort otherwise
  sortTurfsWithPriority: (
    filters: any, turfData: TileCardDetails[], userLocation: any, searchTerm?: string
  ): TileCardDetails[] => {
    const data = [...turfData];
    
    // Active search with location → always use address priority
    if (userLocation && searchTerm?.trim()) {
      return turfProcessor.sortByLocationWithAddressPriority(data, userLocation, searchTerm);
    }
    
    // Apply user's sort selection
    switch (filters.sortBy) {
      case SORT_KEYS.PRICE_LOW_TO_HIGH: return turfProcessor.sortByPrice(data, SortOrder.ASC, getTurfMinPrice);
      case SORT_KEYS.PRICE_HIGH_TO_LOW: return turfProcessor.sortByPrice(data, SortOrder.DESC, getTurfMinPrice);
      case SORT_KEYS.RATING_HIGH_TO_LOW: return turfProcessor.sortByRating(data, SortOrder.DESC);
      case SORT_KEYS.RATING_LOW_TO_HIGH: return turfProcessor.sortByRating(data, SortOrder.ASC);
      case SORT_KEYS.TURF_HEIGHT: return data.sort((a, b) => getTurfMaxHeight(b) - getTurfMaxHeight(a));
      case SORT_KEYS.DISTANCE_NEAREST: 
      default: return turfProcessor.sortByDistance(data, userLocation);
    }
  },

  // Court sorting
  sortCourts: (filters: any, courtData: any[], userLocation: any): any[] => {
    switch (filters.sortBy) {
      case SORT_KEYS.PRICE_HIGH_TO_LOW: return courtProcessor.sortByPrice(courtData, SortOrder.DESC, getCourtMinPrice);
      case SORT_KEYS.RATING_HIGH_TO_LOW: return courtProcessor.sortByRating(courtData, SortOrder.DESC);
      case SORT_KEYS.RATING_LOW_TO_HIGH: return courtProcessor.sortByRating(courtData, SortOrder.ASC);
      case SORT_KEYS.DISTANCE_NEAREST: return courtProcessor.sortByDistance(courtData, userLocation);
      default: return courtProcessor.sortByPrice(courtData, SortOrder.ASC, getCourtMinPrice);
    }
  }
});

export const filterProcessor = createFilterProcessor();
export const sortProcessor = createSortProcessor();

